import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { GROWSPEAK_UI_READ_ONLY_TEMP } from '../../config/growSpeakUi'
import { updateTenantSpeakbotConfig } from '../../api/tenant'
import PasswordInput from '../common/PasswordInput'

const cardStyle  = { background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 18, marginBottom: 12 }
const inputStyle = { width: '100%', padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', color: '#111', outline: 'none', boxSizing: 'border-box' }
const readOnlyFieldStyle = { ...inputStyle, background: '#f5f6f8', color: '#374151', cursor: 'default' }
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }

const ACCENT = '#0F6E56'

function Toggle({ on, onChange }) {
  return (
    <div onClick={onChange} style={{ width: 40, height: 22, borderRadius: 11, background: on ? ACCENT : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ width: 16, height: 16, borderRadius: 8, background: 'white', position: 'absolute', top: 3, left: on ? 21 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  )
}

function ToggleDisplay({ on }) {
  return (
    <div style={{ width: 40, height: 22, borderRadius: 11, background: on ? ACCENT : '#d1d5db', cursor: 'default', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ width: 16, height: 16, borderRadius: 8, background: 'white', position: 'absolute', top: 3, left: on ? 21 : 3, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  )
}

function RowToggle({ label, desc, on, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{label}</p>
        {desc && <p style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{desc}</p>}
      </div>
      <Toggle on={on} onChange={onChange} />
    </div>
  )
}

function RowReadonly({ label, desc, toggleOn }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{label}</p>
        {desc && <p style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{desc}</p>}
      </div>
      <ToggleDisplay on={toggleOn} />
    </div>
  )
}

function formFromConfig(cfg) {
  const c = cfg ?? {}
  return {
    enabled:     c.enabled     ?? false,
    access_code: c.access_code ?? '',
    kiosk_mode:  c.kiosk_mode  ?? false,
    pin_auth:    c.pin_auth    ?? false,
    pin:         c.pin         ?? '',
    languages:   Array.isArray(c.languages) && c.languages.length > 0 ? c.languages : ['en'],
  }
}

function SpeakSettingsReadonly({ speakbotConfig }) {
  const { t } = useTranslation()
  const [form, setForm] = useState(() => formFromConfig(speakbotConfig))

  useEffect(() => {
    setForm(formFromConfig(speakbotConfig))
  }, [speakbotConfig])

  const langOptions = [
    { value: 'en', label: t('language.en') },
    { value: 'vi', label: t('language.vi') },
  ]

  const knownLangLabels = langOptions.filter(l => form.languages.includes(l.value))
  const extraLangCodes = form.languages.filter(code => !langOptions.some(l => l.value === code))

  const pinDisplay = () => {
    const v = form.pin == null ? '' : String(form.pin).trim()
    return v ? '••••••••' : ''
  }

  return (
    <div>
      <div style={cardStyle}>
        <RowReadonly label={t('speakSettings.serviceStatus')} desc={t('speakSettings.serviceStatusDesc')} toggleOn={form.enabled} />
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('speakSettings.accessCode')}</label>
        <input readOnly style={readOnlyFieldStyle} value={form.access_code} />
        <p style={{ fontSize: 11, color: '#888', marginTop: 6 }}>{t('speakSettings.accessCodeHint')}</p>
      </div>

      <div style={cardStyle}>
        <RowReadonly label={t('speakSettings.kioskMode')} desc={t('speakSettings.kioskModeDesc')} toggleOn={form.kiosk_mode} />
      </div>

      <div style={cardStyle}>
        <RowReadonly label={t('speakSettings.pinAuth')} desc={t('speakSettings.pinAuthDesc')} toggleOn={form.pin_auth} />
        {form.pin_auth && (
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>{t('speakSettings.pin')}</label>
            <input readOnly style={readOnlyFieldStyle} type="text" value={pinDisplay()} />
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 10 }}>{t('speakSettings.languages')}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {knownLangLabels.map(l => (
            <span key={l.value}
              style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500, border: `1.5px solid ${ACCENT}`, background: '#e8f5f0', color: ACCENT }}>
              {l.label}
            </span>
          ))}
          {extraLangCodes.map(code => (
            <span key={code}
              style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500, border: `1.5px solid ${ACCENT}`, background: '#e8f5f0', color: ACCENT }}>
              {code}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

function SpeakSettingsEditable({ tenantId, speakbotConfig, onUpdate }) {
  const { t } = useTranslation()
  const cfg = speakbotConfig ?? {}
  const [form, setForm] = useState(() => formFromConfig(cfg))
  const [saved, setSaved]   = useState(false)
  const [saving, setSaving] = useState(false)

  const langOptions = [
    { value: 'en', label: t('language.en') },
    { value: 'vi', label: t('language.vi') },
  ]

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); setSaved(false) }

  function toggleLang(lang) {
    const next = form.languages.includes(lang)
      ? form.languages.filter(l => l !== lang)
      : [...form.languages, lang]
    set('languages', next.length > 0 ? next : [lang])
  }

  async function handleSave() {
    setSaving(true)
    try {
      await updateTenantSpeakbotConfig(tenantId, form)
      setSaved(true)
      onUpdate()
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'save_speak_settings' },
        extra: { tenantId },
      })
    }
    setSaving(false)
  }

  return (
    <div>
      <div style={cardStyle}>
        <RowToggle label={t('speakSettings.serviceStatus')} desc={t('speakSettings.serviceStatusDesc')}
          on={form.enabled} onChange={() => set('enabled', !form.enabled)} />
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('speakSettings.accessCode')}</label>
        <input style={inputStyle} value={form.access_code} onChange={e => set('access_code', e.target.value)} placeholder={t('speakSettings.accessCodePh')} />
        <p style={{ fontSize: 11, color: '#888', marginTop: 6 }}>{t('speakSettings.accessCodeHint')}</p>
      </div>

      <div style={cardStyle}>
        <RowToggle label={t('speakSettings.kioskMode')} desc={t('speakSettings.kioskModeDesc')}
          on={form.kiosk_mode} onChange={() => set('kiosk_mode', !form.kiosk_mode)} />
      </div>

      <div style={cardStyle}>
        <RowToggle label={t('speakSettings.pinAuth')} desc={t('speakSettings.pinAuthDesc')}
          on={form.pin_auth} onChange={() => set('pin_auth', !form.pin_auth)} />
        {form.pin_auth && (
          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>{t('speakSettings.pin')}</label>
            <PasswordInput
              style={inputStyle}
              value={form.pin}
              onChange={e => set('pin', e.target.value)}
              placeholder={t('speakSettings.pinPh')}
              maxLength={8}
              buttonStyle={{ color: '#6b7280' }}
            />
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#111', marginBottom: 10 }}>{t('speakSettings.languages')}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {langOptions.map(l => {
            const active = form.languages.includes(l.value)
            return (
              <button key={l.value} type="button" onClick={() => toggleLang(l.value)}
                style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500, border: `1.5px solid ${active ? ACCENT : '#e0e3f0'}`, background: active ? '#e8f5f0' : 'white', color: active ? ACCENT : '#888', cursor: 'pointer' }}>
                {l.label}
              </button>
            )
          })}
        </div>
      </div>

      <button type="button" onClick={handleSave} disabled={saving}
        style={{ background: saved ? '#16a34a' : ACCENT, color: 'white', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
        {saving ? t('common.saving') : saved ? t('common.saved') : t('common.saveChanges')}
      </button>
    </div>
  )
}

export default function SpeakSettings({ tenantId, speakbotConfig, onUpdate }) {
  if (GROWSPEAK_UI_READ_ONLY_TEMP) {
    return <SpeakSettingsReadonly speakbotConfig={speakbotConfig} />
  }
  return (
    <SpeakSettingsEditable
      tenantId={tenantId}
      speakbotConfig={speakbotConfig}
      onUpdate={onUpdate}
    />
  )
}
